(() => {
  const initialise = root => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const decodeEntities = value => {
      if (typeof value !== 'string') return value;
      const node = document.createElement('textarea');
      node.innerHTML = value;
      return node.value;
    };
    const decodeConfig = value => {
      if (Array.isArray(value)) return value.map(decodeConfig);
      if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, decodeConfig(entry)]));
      return decodeEntities(value);
    };
    const config = decodeConfig(JSON.parse(root.querySelector('[data-quiz-config]').textContent));
    const analytics = window.EveQuizAnalytics?.(config.analytics, root.id);
    const screens = [...root.querySelectorAll('[data-quiz-screen]')];
    const progress = root.querySelector('[data-quiz-progress]');
    const progressBar = root.querySelector('[data-quiz-progress-bar]');
    const progressLabel = root.querySelector('[data-quiz-progress-label]');
    const live = root.querySelector('[data-quiz-live]');
    const state = { hairType: '', hairFallback: '', age: '', frustration: '', routine: '', drying: '', goals: [] };
    const products = {};

    root.querySelectorAll('[data-product-key]').forEach(node => {
      products[node.dataset.productKey] = {
        id: Number(node.dataset.variantId || 0),
        productId: Number(node.dataset.productId || 0),
        handle: node.dataset.productHandle || '',
        title: node.dataset.productTitle,
        image: node.dataset.productImage
      };
    });

    // Reuse Swiper 11, already loaded by layout/theme.liquid.
    const reviewSliders = new Map();
    const updateReviewSliders = () => {
      if (typeof window.Swiper !== 'function') return;
      root.querySelectorAll('[data-result-story]:not([hidden]) .eve-quiz__story-reviews').forEach(element => {
        if (reviewSliders.has(element)) {
          reviewSliders.get(element).update();
          return;
        }
        const wrapper = document.createElement('div');
        wrapper.className = 'swiper-wrapper';
        [...element.children].forEach(card => {
          card.classList.add('swiper-slide');
          wrapper.appendChild(card);
        });
        element.appendChild(wrapper);
        element.classList.add('swiper');
        const slider = new window.Swiper(element, {
          slidesPerView: 1.15,
          spaceBetween: 14,
          breakpoints: { 750: { slidesPerView: 3, spaceBetween: 16 } },
          watchOverflow: true,
          grabCursor: true,
          keyboard: { enabled: true, onlyInViewport: true },
          a11y: { enabled: true },
          speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300
        });
        reviewSliders.set(element, slider);
      });
    };
    const unloadSliders = event => {
      if (!event.target.contains(root)) return;
      reviewSliders.forEach(slider => slider.destroy(true, true));
      reviewSliders.clear();
      document.removeEventListener('shopify:section:unload', unloadSliders);
    };
    document.addEventListener('shopify:section:unload', unloadSliders);

    const questionMap = { q1: 1, age: 2, q2: 3, q3: 4, q4: 5, q5: 6 };
    const totalQuestions = 6;
    let calculationTimer;
    const progressMap = { proof: 3, reassurance: 6, loading: 6, result: 6 };

    const emit = (name, detail = {}) => {
      root.dispatchEvent(new CustomEvent(`eve-quiz:${name}`, { bubbles: true, detail }));
    };

    const dock = root.querySelector('[data-quiz-dock]');
    const stickyHomes = new Map();
    root.querySelectorAll('.eve-quiz__sticky-cta').forEach(node => stickyHomes.set(node, node.parentElement));
    const mobileDock = window.matchMedia('(max-width: 899px)');
    let currentScreen = 'q1';
    const storageKey = 'eveQuizProgress';
    const paramMap = {
      step: 'eq_step',
      age: 'eq_age',
      hairType: 'eq_hair',
      hairFallback: 'eq_hair_fb',
      frustration: 'eq_frust',
      routine: 'eq_routine',
      drying: 'eq_dry',
      goals: 'eq_goals'
    };
    const allowed = {
      age: new Set(['under_18', '18_29', '30_39', '40_49', '50_59', '60_69', '70_plus']),
      hairType: new Set(['wavy', 'curly', 'coily', 'unsure']),
      hairFallback: new Set(['wavy', 'coily']),
      frustration: new Set(['frizz', 'hold', 'dryness', 'weight', 'health']),
      routine: new Set(['none', 'generic', 'not_working', 'upgrade']),
      drying: new Set(['fast', 'slow', 'unknown']),
      goals: new Set(['definition', 'frizz', 'hold', 'health', 'all']),
      step: new Set(['q1', 'age', 'q2', 'proof', 'q3', 'q4', 'q5', 'reassurance', 'result'])
    };
    const screenNeed = {
      q1: 0, age: 1, q2: 2, proof: 3, q3: 3, q4: 4, q5: 5, reassurance: 6, loading: 6, result: 6
    };

    const emptyProgress = () => ({
      step: 'q1',
      hairType: '',
      hairFallback: '',
      age: '',
      frustration: '',
      routine: '',
      drying: '',
      goals: []
    });

    const completedQuestions = () => {
      if (!state.hairType || (state.hairType === 'unsure' && !state.hairFallback)) return 0;
      if (!state.age) return 1;
      if (!state.frustration) return 2;
      if (!state.routine) return 3;
      if (!state.drying) return 4;
      if (!state.goals.length) return 5;
      return totalQuestions;
    };

    const resolveScreen = requested => {
      const completed = completedQuestions();
      // Older saved links can still refer to removed landing and social-proof screens.
      const step = requested === 'landing' ? 'q1' : requested === 'proof' ? 'q3' : requested === 'loading' ? 'result' : requested;
      if (allowed.step.has(step) && completed >= screenNeed[step]) return step;
      return ['q1', 'age', 'q2', 'q3', 'q4', 'q5', 'result'][completed];
    };

    const sanitiseProgress = raw => {
      const next = emptyProgress();
      if (!raw || typeof raw !== 'object') return next;
      if (allowed.age.has(raw.age)) next.age = raw.age;
      if (allowed.hairType.has(raw.hairType)) next.hairType = raw.hairType;
      if (allowed.hairFallback.has(raw.hairFallback)) next.hairFallback = raw.hairFallback;
      if (allowed.frustration.has(raw.frustration)) next.frustration = raw.frustration;
      if (allowed.routine.has(raw.routine)) next.routine = raw.routine;
      if (allowed.drying.has(raw.drying)) next.drying = raw.drying;
      next.goals = [...new Set((Array.isArray(raw.goals) ? raw.goals : String(raw.goals || '').split(',')).map(value => value.trim()).filter(value => allowed.goals.has(value) && value !== 'all'))];
      next.step = raw.step === 'landing' ? 'q1' : allowed.step.has(raw.step) ? raw.step : (raw.step === 'loading' ? 'result' : 'q1');
      return next;
    };

    const readProgressFromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      // Accept common aliases so shared/result links still restore correctly.
      const dryingRaw = params.get(paramMap.drying) || params.get('eq_drying');
      const dryingAliases = { air: 'fast', diffuser: 'slow', towel: 'unknown' };
      const drying = dryingAliases[dryingRaw] || dryingRaw;
      const goals = params.get(paramMap.goals) || params.get('eq_goal');
      if (![paramMap.age, paramMap.step, paramMap.hairType, paramMap.frustration, paramMap.routine, paramMap.drying, paramMap.goals, 'eq_drying', 'eq_goal'].some(key => params.has(key))) {
        return null;
      }
      return sanitiseProgress({
        step: params.get(paramMap.step),
        age: params.get(paramMap.age),
        hairType: params.get(paramMap.hairType),
        hairFallback: params.get(paramMap.hairFallback),
        frustration: params.get(paramMap.frustration),
        routine: params.get(paramMap.routine),
        drying,
        goals
      });
    };

    const writeProgressUrl = (snapshot, replace) => {
      const url = new URL(window.location.href);
      Object.values(paramMap).forEach(key => url.searchParams.delete(key));
      const hasAnswers = Boolean(snapshot.hairType || snapshot.frustration || snapshot.routine || snapshot.drying || snapshot.goals.length);
      if (snapshot.step !== 'q1' || hasAnswers) {
        url.searchParams.set(paramMap.step, snapshot.step === 'loading' ? 'result' : snapshot.step);
        if (snapshot.age) url.searchParams.set(paramMap.age, snapshot.age);
        if (snapshot.hairType) url.searchParams.set(paramMap.hairType, snapshot.hairType);
        if (snapshot.hairFallback) url.searchParams.set(paramMap.hairFallback, snapshot.hairFallback);
        if (snapshot.frustration) url.searchParams.set(paramMap.frustration, snapshot.frustration);
        if (snapshot.routine) url.searchParams.set(paramMap.routine, snapshot.routine);
        if (snapshot.drying) url.searchParams.set(paramMap.drying, snapshot.drying);
        if (snapshot.goals.length) url.searchParams.set(paramMap.goals, snapshot.goals.join(','));
      }
      const next = `${url.pathname}${url.search}${url.hash}`;
      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (next === current) return;
      history[replace ? 'replaceState' : 'pushState']({ eveQuiz: true }, '', next);
    };

    const persist = (step, replace = false) => {
      syncState();
      const snapshot = {
        step: step === 'loading' ? currentScreen : step,
        age: state.age,
        hairType: state.hairType,
        hairFallback: state.hairFallback,
        frustration: state.frustration,
        routine: state.routine,
        drying: state.drying,
        goals: [...state.goals]
      };
      if (step === 'loading') return;
      snapshot.step = step;
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(snapshot));
      } catch (error) {}
      writeProgressUrl(snapshot, replace);
    };

    const applyAnswers = snapshot => {
      const values = {
        age: snapshot.age,
        hairType: snapshot.hairType,
        hairFallback: snapshot.hairFallback,
        frustration: snapshot.frustration,
        routine: snapshot.routine,
        drying: snapshot.drying
      };
      Object.entries(values).forEach(([key, value]) => {
        root.querySelectorAll(`[data-answer="${key}"]`).forEach(input => {
          input.checked = Boolean(value) && input.value === value;
        });
      });
      root.querySelectorAll('[data-answer="goals"]').forEach(input => {
        input.checked = snapshot.goals.includes(input.value) || (input.value === 'all' && snapshot.goals.length === 4);
      });
      const fallback = root.querySelector('[data-hair-fallback]');
      const open = snapshot.hairType === 'unsure';
      if (fallback) fallback.hidden = !open;
      syncState();
    };

    const restoreProgress = (snapshot, replace = true) => {
      applyAnswers(snapshot);
      const screen = resolveScreen(snapshot.step);
      if (screen === 'result' && completedQuestions() === totalQuestions) renderResult();
      show(screen, { restore: true, replace });
    };

    const pinSticky = screenName => {
      currentScreen = screenName;
      root.querySelectorAll('.eve-quiz__sticky-cta').forEach(node => {
        const home = stickyHomes.get(node);
        const screen = home?.closest('[data-quiz-screen]');
        const shouldPin = Boolean(dock && mobileDock.matches && screenName !== 'loading' && screenName !== 'result' && screen?.dataset.quizScreen === screenName);
        if (shouldPin) dock.appendChild(node);
        else if (home && node.parentElement === dock) home.appendChild(node);
      });
    };

    const show = (name, options = {}) => {
      if (name !== 'loading') window.clearTimeout(calculationTimer);
      const target = screens.find(screen => screen.dataset.quizScreen === name);
      if (!target) return;

      root.dataset.activeScreen = name;
      root.classList.toggle('eve-quiz--progress-visible', name !== 'loading' && name !== 'result');
      screens.forEach(screen => {
        const active = screen === target;
        screen.classList.toggle('is-active', active);
        screen.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      if (name === 'loading' || name === 'result') {
        progress.hidden = true;
      } else {
        const question = questionMap[name];
        const completed = question || progressMap[name] || 0;
        progress.hidden = false;
        progressBar.style.width = `${Math.min(completed / totalQuestions * 100, 100)}%`;
        progressLabel.textContent = completed
          ? config.progress.replace('[current]', String(completed)).replace('[total]', String(totalQuestions))
          : '';
      }

      if (name === 'result') updateReviewSliders();
      pinSticky(name);
      persist(name, Boolean(options.restore || options.replace));

      if (!options.restore) {
        const heading = target.querySelector('h1, h2');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          try {
            heading.focus({ preventScroll: true });
          } catch (error) {
            heading.focus();
          }
          live.textContent = heading.textContent.trim();
        }

        const stickyBar = document.getElementById('announcementBar');
        const stickyOffset = (stickyBar?.offsetHeight || 0) + 8;
        const top = root.getBoundingClientRect().top + window.scrollY - stickyOffset;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      }

      emit('screen', { screen: name, question: questionMap[name] || null });
    };

    const selectedValue = key => root.querySelector(`[data-answer="${key}"]:checked`)?.value || '';
    const selectedValues = key => [...root.querySelectorAll(`[data-answer="${key}"]:checked`)].map(input => input.value);

    const syncState = () => {
      state.age = selectedValue('age');
      state.hairType = selectedValue('hairType');
      state.hairFallback = selectedValue('hairFallback');
      state.frustration = selectedValue('frustration');
      state.routine = selectedValue('routine');
      state.drying = selectedValue('drying');
      state.goals = selectedValues('goals').filter(value => value !== 'all');
    };

    const validate = screen => {
      syncState();
      const error = screen.querySelector('[data-quiz-error]');
      let valid = true;
      if (screen.dataset.quizScreen === 'age') valid = Boolean(state.age);
      if (screen.dataset.quizScreen === 'q1') valid = Boolean(state.hairType && (state.hairType !== 'unsure' || state.hairFallback));
      if (screen.dataset.quizScreen === 'q2') valid = Boolean(state.frustration);
      if (screen.dataset.quizScreen === 'q3') valid = Boolean(state.routine);
      if (screen.dataset.quizScreen === 'q4') valid = Boolean(state.drying);
      if (screen.dataset.quizScreen === 'q5') valid = state.goals.length > 0;
      if (error) error.textContent = valid ? '' : config.errorRequired;
      return valid;
    };

    root.querySelectorAll('[data-answer="hairType"]').forEach(input => {
      input.addEventListener('change', () => {
        const fallback = root.querySelector('[data-hair-fallback]');
        const open = input.checked && input.value === 'unsure';
        fallback.hidden = !open;
        fallback.removeAttribute('disabled');
        root.querySelectorAll('[data-answer="hairFallback"]').forEach(item => {
          item.disabled = false;
          if (!open) item.checked = false;
        });
        if (open) {
          try {
            fallback.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          } catch (error) {
            fallback.scrollIntoView();
          }
        }
      });
    });

    root.querySelectorAll('[data-answer="goals"]').forEach(input => {
      input.addEventListener('change', () => {
        const all = root.querySelector('[data-answer="goals"][value="all"]');
        const choices = [...root.querySelectorAll('[data-answer="goals"]')].filter(item => item.value !== 'all');
        if (input.value === 'all') {
          choices.forEach(item => { item.checked = input.checked; });
        } else if (!input.checked) {
          all.checked = false;
        } else {
          all.checked = choices.every(item => item.checked);
        }
      });
    });

    root.querySelectorAll('[data-answer]').forEach(input => {
      input.addEventListener('change', () => persist(currentScreen, true));
    });

    root.querySelectorAll('[data-quiz-go]').forEach(button => {
      button.addEventListener('click', () => show(button.dataset.quizGo));
    });

    root.querySelectorAll('[data-quiz-next]').forEach(button => {
      button.addEventListener('click', () => {
        const screen = button.closest('[data-quiz-screen]') || root.querySelector('[data-quiz-screen].is-active');
        if (!screen || !validate(screen)) return;
        emit('answer', { question: Number(screen.dataset.question), answers: { ...state } });
        show(button.dataset.quizNext);
      });
    });

    const frizzLevel = frustration => {
      if (frustration === 'frizz' || frustration === 'hold') return 'high';
      if (frustration === 'weight') return 'low';
      return 'medium';
    };

    const moistureFor = cluster => {
      if (cluster === 'wavy') return { level: 'medium', position: 50 };
      if (cluster === 'curly') return { level: 'high', position: 70 };
      return { level: 'high', position: 85 };
    };

    const resultCluster = () => state.hairType === 'unsure' ? state.hairFallback : state.hairType;

    // Product order follows Quiz updates.pdf; all three bundles contain eight items.
    const routineFor = cluster => {
      const routines = {
        wavy: ['shampoo', 'conditioner', 'cream', 'foam', 'gel', 'oil', 'refresher', 'perfume'],
        curly: ['shampoo', 'conditioner', 'mask', 'leave_in', 'cream', 'gel', 'oil', 'refresher'],
        coily: ['shampoo', 'conditioner', 'mask', 'leave_in', 'cream', 'oil', 'refresher', 'perfume']
      };
      return routines[cluster].map(key => {
        const description = cluster === 'wavy' && ['conditioner', 'cream', 'gel'].includes(key)
          ? `${key}_wavy` : cluster === 'coily' && ['mask', 'oil'].includes(key) ? `${key}_coily` : key;
        return [key, description];
      });
    };

    const diagnosisFor = (cluster, result) => {
      const goals = state.goals;
      if (cluster === 'wavy') {
        if (goals.includes('hold')) return result.holdDiagnosis;
        if (goals.includes('frizz')) return result.defaultDiagnosis;
        if (goals.includes('definition')) return result.definitionDiagnosis;
        return result.defaultDiagnosis;
      }
      if (cluster === 'curly') {
        if (goals.includes('frizz')) return result.frizzDiagnosis;
        if (goals.includes('hold')) return result.holdDiagnosis;
        return result.defaultDiagnosis;
      }
      if (goals.includes('definition')) return result.definitionDiagnosis;
      if (goals.includes('health')) return result.healthDiagnosis;
      return result.defaultDiagnosis;
    };

    const escapeHtml = value => {
      const node = document.createElement('span');
      node.textContent = value || '';
      return node.innerHTML;
    };

    const gaugeMarkup = (label, level, markerPosition) => {
      const positions = { low: 8, medium: 50, high: 92 };
      const position = markerPosition ?? positions[level];
      const readable = config.levels[level];
      return `
        <div class="eve-quiz__gauge-heading">
          <span class="eve-quiz__gauge-title">${escapeHtml(label)}</span>
          <strong class="eve-quiz__gauge-value">${escapeHtml(readable)}</strong>
        </div>
        <div class="eve-quiz__gauge-track" role="img" aria-label="${escapeHtml(`${label}: ${readable}`)}">
          <span class="eve-quiz__gauge-marker" style="left:${position}%"></span>
        </div>
        <div class="eve-quiz__gauge-scale" aria-hidden="true"><span>${escapeHtml(config.levels.low)}</span><span>${escapeHtml(config.levels.medium)}</span><span>${escapeHtml(config.levels.high)}</span></div>
      `;
    };

    const renderResult = () => {
      syncState();
      const cluster = resultCluster();
      const result = config.results[cluster];
      const routine = routineFor(cluster);
      const frizz = frizzLevel(state.frustration);
      const moisture = moistureFor(cluster);

      root.querySelectorAll('[data-result-story]').forEach(story => {
        story.hidden = story.dataset.resultStory !== cluster;
      });
      root.querySelector('[data-result-title]').textContent = result.title;
      root.querySelector('[data-result-diagnosis]').textContent = diagnosisFor(cluster, result);
      root.querySelector('[data-result-type]').textContent = result.type;
      root.querySelector('[data-result-potential]').textContent = result.potential;
      root.querySelector('[data-result-frizz]').innerHTML = gaugeMarkup(config.gaugeLabels.frizz, frizz);
      root.querySelector('[data-result-moisture]').innerHTML = gaugeMarkup(config.gaugeLabels.moisture, moisture.level, moisture.position);

      root.querySelector('[data-result-routine]').innerHTML = routine.map(([key, description]) => {
        const product = products[key];
        const visual = product.image
          ? `<img src="${escapeHtml(product.image)}" alt="" width="120" height="120">`
          : `<span aria-hidden="true">${escapeHtml(product.title.charAt(0))}</span>`;
        return `<li class="eve-quiz__routine-item"><span class="eve-quiz__routine-image">${visual}</span><div class="eve-quiz__routine-copy"><strong>${escapeHtml(product.title)}</strong><p>${escapeHtml(config.descriptions[description])}</p></div></li>`;
      }).join('');

      root.querySelector('[data-result-tips]').innerHTML = result.tips.map(tip => `<li>${escapeHtml(tip)}</li>`).join('');
      const review = root.querySelector('[data-result-review]');
      review.hidden = !result.review;
      review.textContent = result.review || '';
      root.dataset.resultCluster = cluster;
      root.dataset.resultProducts = routine.map(([key]) => key).join(',');
      emit('result', { cluster, frizz, moisture: moisture.level, products: routine.map(([key]) => key), answers: { ...state } });
    };

    root.querySelector('[data-quiz-calculate]').addEventListener('click', () => {
      if (currentScreen === 'loading' || currentScreen === 'result') return;
      syncState();
      if (completedQuestions() !== totalQuestions) return;
      const cluster = resultCluster();
      show('loading', { replace: true });
      analytics?.complete({ cluster, products: routineFor(cluster).map(([key]) => key), answers: { ...state, goals: [...state.goals] } });
      calculationTimer = window.setTimeout(() => {
        renderResult();
        show('result');
      }, 2400);
    });

    const ctaButton = root.querySelector('[data-add-routine]');
    const cartStatus = root.querySelector('[data-cart-status]');

    const setCtaState = mode => {
      ctaButton.querySelector('[data-cta-idle]').hidden = mode !== 'idle';
      ctaButton.querySelector('[data-cta-busy]').hidden = mode !== 'busy';
      ctaButton.querySelector('[data-cta-done]').hidden = mode !== 'done';
      ctaButton.classList.toggle('is-busy', mode === 'busy');
      ctaButton.classList.toggle('is-done', mode === 'done');
      ctaButton.toggleAttribute('aria-busy', mode === 'busy');
      ctaButton.disabled = mode === 'busy';
    };

    const setCartStatus = (message, isError = false) => {
      cartStatus.textContent = message || '';
      cartStatus.classList.toggle('is-error', Boolean(isError && message));
    };

    const recommendedProducts = () => {
      const keys = (root.dataset.resultProducts || '').split(',').filter(Boolean);
      return keys.map(key => products[key]).filter(Boolean);
    };

    const bundleDestination = () => {
      if (config.bundleUrl) return config.bundleUrl;
      const root = window.Shopify?.routes?.root || '/';
      return `${root}pages/bundle`;
    };

    const goToBundle = items => {
      const variantIds = [...new Set(items.map(item => item.id).filter(Boolean))];
      const productIds = [...new Set(items.map(item => item.productId).filter(Boolean))];
      const handles = [...new Set(items.map(item => item.handle).filter(Boolean))];
      const payload = {
        variantIds,
        productIds,
        handles,
        cluster: root.dataset.resultCluster || ''
      };

      try {
        sessionStorage.setItem('eveQuizPreselect', JSON.stringify(payload));
      } catch (error) {
        // Storage can be blocked; URL params are the fallback.
      }

      const url = new URL(bundleDestination(), window.location.origin);
      if (variantIds.length) url.searchParams.set('quiz_variants', variantIds.join(','));
      if (productIds.length) url.searchParams.set('quiz_products', productIds.join(','));
      if (handles.length) url.searchParams.set('quiz_handles', handles.join(','));
      emit('bundle-redirect', payload);
      window.location.assign(url.toString());
    };

    ctaButton.addEventListener('click', async () => {
      const items = recommendedProducts();
      const ids = [...new Set(items.map(item => item.id).filter(Boolean))];

      if (bundleDestination()) {
        if (items.length !== 8 || items.some(item => !item.id || !item.productId || !item.handle)) {
          setCtaState('idle');
          setCartStatus(config.errorProducts, true);
          return;
        }
        goToBundle(items);
        return;
      }

      if (!ids.length) {
        setCtaState('idle');
        setCartStatus(config.errorProducts, true);
        return;
      }

      setCtaState('busy');
      setCartStatus(config.adding);
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: ids.map(id => ({ id, quantity: 1 })) })
        });
        if (!response.ok) throw new Error('Cart request failed');
        const added = await response.json();
        setCtaState('done');
        setCartStatus(config.added);
        document.documentElement.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: added.items || [added] } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        emit('add', { cluster: root.dataset.resultCluster, variantIds: ids });
      } catch (error) {
        setCtaState('idle');
        setCartStatus(config.errorCart, true);
      }
    });

    root.querySelector('[data-quiz-restart]').addEventListener('click', () => {
      analytics?.restart();
      root.querySelectorAll('input').forEach(input => { input.checked = false; });
      Object.assign(state, { hairType: '', hairFallback: '', age: '', frustration: '', routine: '', drying: '', goals: [] });
      const fallback = root.querySelector('[data-hair-fallback]');
      fallback.hidden = true;
      fallback.removeAttribute('disabled');
      setCtaState('idle');
      setCartStatus('');
      try {
        sessionStorage.removeItem(storageKey);
      } catch (error) {}
      emit('restart');
      show('q1', { replace: true });
    });

    window.addEventListener('popstate', () => {
      const snapshot = readProgressFromUrl() || emptyProgress();
      restoreProgress(snapshot, true);
    });

    // A normal quiz link starts at hair type; explicit question/result URLs can still resume.
    const initialProgress = readProgressFromUrl();
    if (initialProgress) {
      restoreProgress(initialProgress, true);
    } else {
      show('q1', { restore: true, replace: true });
    }
    const handleViewportChange = () => pinSticky(currentScreen);
    if (mobileDock.addEventListener) mobileDock.addEventListener('change', handleViewportChange);
    else if (mobileDock.addListener) mobileDock.addListener(handleViewportChange);
    emit('ready');
  };

  const initialiseAll = scope => scope.querySelectorAll('[data-eve-quiz]').forEach(initialise);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll(document));
  } else {
    initialiseAll(document);
  }
  document.addEventListener('shopify:section:load', event => initialiseAll(event.target));
})();
